// Library Methods
import { useLayoutEffect, useRef } from "react";

// Components
import Box from "@mui/material/Box";
import Drawer, { DrawerProps } from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { PaperProps } from "@mui/material/Paper";
import { CloseIcon } from "src/assets/icons";

type DefaultDrawerType = {
  anchor?: DrawerProps["anchor"];
  title: string;
  isOpen: boolean;
  onClose: () => void;
  showCloseButton?: boolean;
  children: React.ReactNode;
  customStyles: PaperProps["sx"];
  extraHeaderButtons?: Array<JSX.Element>;
};

const DefaultDrawer = ({
  anchor = "right",
  title,
  isOpen,
  onClose,
  showCloseButton = true,
  children,
  customStyles,
  extraHeaderButtons = undefined,
}: DefaultDrawerType) => {
  const paperRef = useRef<HTMLDivElement | null>(null);

  // On close, drop the drawer focus before MUI marks it as hidden
  // This prevents a console warning
  useLayoutEffect(() => {
    if (isOpen) return;

    const focused = document.activeElement;
    if (focused instanceof HTMLElement && paperRef.current?.contains(focused)) {
      focused.blur();
    }
  }, [isOpen]);

  return (
    <Drawer
      anchor={anchor}
      open={isOpen}
      onClose={onClose}
      disableRestoreFocus
      PaperProps={{
        ref: paperRef,
        sx: { ...customStyles },
      }}
    >
      <Box
        id="default-drawer-header"
        position="sticky"
        top={0}
        zIndex={100}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          padding: "12px 16px",
          color: "#FFF",
          backgroundColor: "#202338",
          position: "sticky",
          top: 0,
        }}
      >
        <Typography fontWeight={600} fontSize="14px">
          {title}
        </Typography>

        <Box sx={{ display: "flex", gap: "8px" }}>
          {extraHeaderButtons?.map((extraButton, index) => (
            <div key={`extra-button-${index}`}>{extraButton}</div>
          ))}

          {showCloseButton && (
            <IconButton
              aria-label="close-drawer"
              onClick={onClose}
              sx={{
                color: "#FFF",
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      {children}
    </Drawer>
  );
};

export default DefaultDrawer;
